import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import type { SupportedLocale } from "./localization";

type Lead = {
  id: string;
  organization_id: string | null;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  country_code: string;
  status: string;
  priority: string;
  assigned_to: string | null;
  created_by: string;
  updated_at: string;
  archive_reason: string | null;
};
type Task = {
  id: string;
  lead_id: string | null;
  organization_id: string | null;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  priority: string;
  status: string;
  assigned_to: string;
  archive_reason: string | null;
  completed_at?: string | null;
  archived_at?: string | null;
  withdrawn_at?: string | null;
  withdrawn_by?: string | null;
  withdrawal_reason?: string | null;
  withdrawal_review_status?: string | null;
  withdrawal_reviewed_at?: string | null;
  withdrawal_reviewed_by?: string | null;
};
type Plan = {
  id: string;
  name: string;
  description: string | null;
  is_trial: boolean;
  trial_days: number | null;
  active: boolean;
};
type TaskAssignee = { user_id: string; display_name: string };
type TaskEvent = { id: number; actor_user_id: string; event_type: string; message: string | null; created_at: string };
type TaskAction = "withdraw" | "reactivate" | "approve" | "reopen";
type Tab = "leads" | "pipeline" | "tasks";
const leadLabels: Record<string, string> = {
  new: "Novo",
  to_contact: "Por contactar",
  contacted: "Contactado",
  qualifying: "Em qualificação",
  meeting: "Reunião agendada",
  proposal: "Proposta enviada",
  trial: "Em período experimental",
  won: "Ganho",
  lost: "Perdido",
  archived: "Arquivado",
};
const priorities: Record<string, string> = {
  low: "Baixa",
  normal: "Normal",
  high: "Alta",
  urgent: "Urgente",
};
const isoLocal = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

export function TaskCenter({
  userId,
  isMaster,
}: {
  userId: string;
  isMaster: boolean;
}) {
  const [tasks, setTasks] = useState<Task[]>([]),
    [open, setOpen] = useState(false),
    [selected, setSelected] = useState<Task | null>(null),
    [message, setMessage] = useState<string | null>(null),
    [canManageTasks, setCanManageTasks] = useState(isMaster),
    [statusFilter, setStatusFilter] = useState("all"),
    [assigneeFilter, setAssigneeFilter] = useState("all"),
    [periodFilter, setPeriodFilter] = useState("all"),
    [dateFrom, setDateFrom] = useState(""),
    [dateTo, setDateTo] = useState(""),
    [assignees, setAssignees] = useState<TaskAssignee[]>([]),
    [page, setPage] = useState(0),
    [totalTasks, setTotalTasks] = useState(0),
    [urgent, setUrgent] = useState<Task | null>(null),
    [events, setEvents] = useState<TaskEvent[]>([]),
    [taskAction, setTaskAction] = useState<TaskAction | null>(null),
    [actionSaving, setActionSaving] = useState(false);
  const pageSize = 50;
  const dateBounds = () => {
    const start = new Date(), end = new Date();
    start.setHours(0, 0, 0, 0); end.setHours(23, 59, 59, 999);
    if (periodFilter === "tomorrow") { start.setDate(start.getDate() + 1); end.setDate(end.getDate() + 1); }
    if (periodFilter === "week") { end.setDate(start.getDate() + 6); }
    if (periodFilter === "month") { start.setDate(1); end.setMonth(end.getMonth() + 1, 0); }
    if (periodFilter === "custom") return { from: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : null, to: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : null };
    return periodFilter === "all" ? { from: null, to: null } : { from: start.toISOString(), to: end.toISOString() };
  };
  const load = async () => {
    const nowIso = new Date().toISOString();
    let query = supabase
      .from("commercial_tasks")
      .select(
        "id,lead_id,organization_id,title,description,starts_at,ends_at,priority,status,assigned_to,archive_reason,completed_at,archived_at,withdrawn_at,withdrawn_by,withdrawal_reason,withdrawal_review_status,withdrawal_reviewed_at,withdrawal_reviewed_by",
        { count: "exact" },
      )
      .order("starts_at")
      .range(page * pageSize, page * pageSize + pageSize - 1);
    if (assigneeFilter !== "all") query = query.eq("assigned_to", assigneeFilter);
    if (statusFilter === "overdue") query = query.in("status", ["open", "in_progress"]).lt("starts_at", nowIso);
    if (statusFilter === "active") query = query.in("status", ["open", "in_progress"]).gte("starts_at", nowIso);
    if (statusFilter === "completed") query = query.eq("status", "completed");
    if (statusFilter === "withdrawn_pending") query = query.eq("status", "withdrawn").eq("withdrawal_review_status", "pending");
    if (statusFilter === "withdrawn_approved") query = query.eq("status", "withdrawn").eq("withdrawal_review_status", "approved");
    const bounds = dateBounds();
    if (bounds.from) query = query.gte("starts_at", bounds.from);
    if (bounds.to) query = query.lte("starts_at", bounds.to);
    const { data, count, error } = await query;
    if (error) { setMessage(error.message); return; }
    setTasks((data ?? []) as Task[]);
    setTotalTasks(count ?? 0);
  };
  const loadCard = async () => {
    const { data } = await supabase.from("commercial_tasks")
      .select("id,title,starts_at,priority,status", { count: "exact" })
      .in("status", ["open", "in_progress"]).order("starts_at").limit(1);
    setUrgent(((data ?? [])[0] as Task | undefined) ?? null);
  };
  useEffect(() => {
    if (isMaster) return;
    void supabase
      .from("commercial_team_members")
      .select("role_key")
      .eq("user_id", userId)
      .eq("active", true)
      .eq("role_key", "manager")
      .limit(1)
      .then(({ data }) => setCanManageTasks((data?.length ?? 0) > 0));
  }, [isMaster, userId]);
  useEffect(() => {
    void loadCard();
    const refresh = () => {
      void load();
      void loadCard();
    };
    window.addEventListener("vorno:tasks-changed", refresh);
    const channel = supabase
      .channel(`commercial-tasks-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "commercial_tasks" },
        refresh,
      )
      .subscribe();
    return () => {
      window.removeEventListener("vorno:tasks-changed", refresh);
      void supabase.removeChannel(channel);
    };
  }, [userId]);
  useEffect(() => { void load(); }, [assigneeFilter, statusFilter, periodFilter, dateFrom, dateTo, page]);
  useEffect(() => {
    if (!canManageTasks) return;
    void supabase.from("commercial_team_members").select("user_id").eq("active", true).then(async ({ data }) => {
      const ids = [...new Set((data ?? []).map((member) => member.user_id))];
      if (isMaster && !ids.includes(userId)) ids.push(userId);
      if (ids.length === 0) return;
      const { data: profiles } = await supabase.from("profiles").select("user_id,display_name").in("user_id", ids).order("display_name");
      setAssignees((profiles ?? []).map((profile) => ({ user_id: profile.user_id, display_name: profile.display_name ?? "Utilizador sem nome" })));
    });
  }, [canManageTasks, isMaster, userId]);
  useEffect(() => { setPage(0); }, [assigneeFilter, statusFilter, periodFilter, dateFrom, dateTo]);
  useEffect(() => {
    if (!selected) { setEvents([]); return; }
    void supabase.from("commercial_task_events").select("id,actor_user_id,event_type,message,created_at").eq("task_id", selected.id).order("created_at", { ascending: false }).then(({ data }) => setEvents((data ?? []) as TaskEvent[]));
  }, [selected?.id]);
  const now = Date.now();
  const filterTasks = (items: Task[], key: string) =>
    !canManageTasks || statusFilter === "all" || statusFilter === key
      ? items
      : [];
  const taskGroups = [
    {
      key: "overdue",
      label: "Urgentes · atrasadas",
      items: filterTasks(tasks
        .filter(
          (x) =>
            (x.status === "open" || x.status === "in_progress") &&
            new Date(x.starts_at).getTime() < now,
        )
        .sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        ), "overdue"),
    },
    {
      key: "pending",
      label: "Pendentes e agendadas",
      items: filterTasks(tasks
        .filter(
          (x) =>
            (x.status === "open" || x.status === "in_progress") &&
            new Date(x.starts_at).getTime() >= now,
        )
        .sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        ), "active"),
    },
    {
      key: "completed",
      label: "Concluídas",
      items: filterTasks(tasks
        .filter((x) => x.status === "completed")
        .sort(
          (a, b) =>
            new Date(b.completed_at ?? b.starts_at).getTime() -
            new Date(a.completed_at ?? a.starts_at).getTime(),
        ), "completed"),
    },
    {
      key: "withdrawn",
      label: "Desistidas · aguardam revisão",
      items: filterTasks(tasks
        .filter((x) => x.status === "withdrawn" && x.withdrawal_review_status === "pending")
        .sort(
          (a, b) =>
            new Date(b.withdrawn_at ?? b.starts_at).getTime() -
            new Date(a.withdrawn_at ?? a.starts_at).getTime(),
        ), "withdrawn_pending"),
    },
    {
      key: "withdrawn-approved",
      label: "Desistências revistas",
      items: filterTasks(tasks
        .filter((x) => x.status === "withdrawn" && x.withdrawal_review_status === "approved")
        .sort((a, b) => new Date(b.withdrawal_reviewed_at ?? b.starts_at).getTime() - new Date(a.withdrawal_reviewed_at ?? a.starts_at).getTime()), "withdrawn_approved"),
    },
  ];
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    const f = new FormData(e.currentTarget),
      start = String(f.get("starts_at")),
      end = String(f.get("ends_at") || "");
    const { data, error } = await supabase
      .from("commercial_tasks")
      .update({
        title: String(f.get("title")),
        description: String(f.get("description") || "") || null,
        starts_at: new Date(start).toISOString(),
        ends_at: end ? new Date(end).toISOString() : null,
        priority: String(f.get("priority")),
      })
      .eq("id", selected.id)
      .select("*")
      .single();
    if (error) {
      setMessage(error.message);
      return;
    }
    setSelected(data as Task);
    setMessage("Tarefa atualizada.");
    void load();
  };
  const finish = async () => {
    if (!selected) return;
    await supabase
      .from("commercial_tasks")
      .update({ status: "completed", completed_at: new Date().toISOString() })
      .eq("id", selected.id);
    setMessage("Tarefa concluída.");
    setSelected(null);
    void load();
  };
  const withdraw = async (reason: string) => {
    if (!selected) return;
    const { error } = await supabase
      .from("commercial_tasks")
      .update({
        status: "withdrawn",
        withdrawn_at: new Date().toISOString(),
        withdrawn_by: userId,
        withdrawal_reason: reason.trim(),
        withdrawal_review_status: "pending",
      })
      .eq("id", selected.id);
    if (error) throw error;
    setMessage("Desistência registada e enviada para revisão.");
    setSelected(null);
    void load();
  };
  const reactivate = async (note: string) => {
    if (!selected) return;
    const { data, error } = await supabase
      .from("commercial_tasks")
      .update({
        status: "open",
        withdrawn_at: null,
        withdrawn_by: null,
        withdrawal_reason: null,
        withdrawal_review_status: null,
        withdrawal_reviewed_at: null,
        withdrawal_reviewed_by: null,
        reactivated_at: new Date().toISOString(),
        reactivated_by: userId,
        reactivation_message: note.trim(),
      })
      .eq("id", selected.id)
      .select("id,status")
      .single();
    if (error || !data) {
      setMessage(error?.message ?? "Não foi possível reativar a tarefa.");
      return;
    }
    setMessage("Tarefa reativada.");
    setSelected(null);
    await load();
    window.dispatchEvent(new CustomEvent("vorno:tasks-changed"));
  };
  const approveWithdrawal = async (note: string) => {
    if (!selected || !canManageTasks) return;
    const { error } = await supabase.from("commercial_tasks").update({
      withdrawal_review_status: "approved",
      withdrawal_reviewed_at: new Date().toISOString(),
      withdrawal_reviewed_by: userId,
      withdrawal_review_note: note.trim() || null,
    }).eq("id", selected.id);
    if (error) { setMessage(error.message); return; }
    setMessage("Desistência revista e aprovada.");
    setSelected(null);
    void load();
  };
  const reopen = async (note: string) => {
    if (!selected || !canManageTasks) return;
    const { error } = await supabase.from("commercial_tasks").update({ status: "open", completed_at: null, reactivated_at: new Date().toISOString(), reactivated_by: userId, reactivation_message: note.trim() }).eq("id", selected.id);
    if (error) throw error;
    setMessage("Tarefa concluída reaberta."); setSelected(null); await load(); window.dispatchEvent(new CustomEvent("vorno:tasks-changed"));
  };
  const submitTaskAction = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); if (!taskAction) return;
    const note = String(new FormData(event.currentTarget).get("note") ?? "").trim();
    if (taskAction !== "approve" && !note) return;
    setActionSaving(true);
    try {
      if (taskAction === "withdraw") await withdraw(note);
      if (taskAction === "reactivate") await reactivate(note);
      if (taskAction === "approve") await approveWithdrawal(note);
      if (taskAction === "reopen") await reopen(note);
      setTaskAction(null);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Não foi possível concluir a ação."); }
    finally { setActionSaving(false); }
  };
  return (
    <>
      <button
        className={`sidebar-task-card ${urgent && new Date(urgent.starts_at).getTime() < now ? "priority-urgent" : urgent ? `priority-${urgent.priority}` : ""}`}
        onClick={() => setOpen(true)}
      >
        <span className="sidebar-task-title">
          <b>Tarefas</b>
        </span>
        {urgent ? (
          <>
            <strong>{urgent.title}</strong>
            <time>
              {new Date(urgent.starts_at).toLocaleString("pt-PT", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </time>
          </>
        ) : (
                <small>Sem tarefas pendentes</small>
        )}
      </button>
      {open && (
        <div className="modal-backdrop task-center-backdrop">
          <section
            className="task-center-modal"
            role="dialog"
            aria-modal="true"
          >
            <header>
              <div>
                <p className="eyebrow">Tarefas</p>
                <h2>Centro de tarefas</h2>
                <p>
                  {isMaster
                    ? "Visão global das tarefas da operação comercial."
                    : "As suas tarefas e agendamentos."}
                </p>
              </div>
              <div className="task-center-header-actions">
                {canManageTasks && (
                  <div className="task-management-filters">
                    <label>Comercial<select aria-label="Comercial" value={assigneeFilter} onChange={(event) => setAssigneeFilter(event.target.value)}><option value="all">Todos os comerciais</option>{assignees.map((person) => <option key={person.user_id} value={person.user_id}>{person.display_name}</option>)}</select></label>
                    <label>Estado<select aria-label="Estado" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">Todos os estados</option><option value="overdue">Atrasadas</option><option value="active">Ativas e agendadas</option><option value="completed">Concluídas</option><option value="withdrawn_pending">Desistidas · aguardam revisão</option><option value="withdrawn_approved">Desistências revistas</option></select></label>
                    <label>Período<select aria-label="Período" value={periodFilter} onChange={(event) => setPeriodFilter(event.target.value)}><option value="all">Todas as datas</option><option value="today">Hoje</option><option value="tomorrow">Amanhã</option><option value="week">Próximos 7 dias</option><option value="month">Este mês</option><option value="custom">Intervalo personalizado</option></select></label>
                    {periodFilter === "custom" && <div className="task-custom-dates"><label>De<input aria-label="Data inicial" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label><label>Até<input aria-label="Data final" type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} /></label></div>}
                  </div>
                )}
                <button
                  className="icon-button"
                  onClick={() => {
                    setOpen(false);
                    setSelected(null);
                  }}
                >
                  ×
                </button>
              </div>
            </header>
            {message && (
              <button className="notice" onClick={() => setMessage(null)}>
                {message} ×
              </button>
            )}
            <div className="task-center-layout">
              <div className="task-center-list">
                {tasks.length === 0 ? (
                  <p className="muted">Ainda não existem tarefas.</p>
                ) : (
                  taskGroups.map(
                    (group) =>
                      group.items.length > 0 && (
                        <section
                          className={`task-status-group ${group.key}`}
                          key={group.key}
                        >
                          <div className="task-list-heading">
                            <strong>{group.label}</strong>
                            <span>{group.items.length}</span>
                          </div>
                          {group.items.map((task) => (
                            <button
                              key={task.id}
                              className={`${selected?.id === task.id ? "selected" : ""} priority-${group.key === "overdue" ? "urgent" : task.priority}`}
                              onClick={() => setSelected(task)}
                            >
                              <i />
                              <div>
                                <strong>{task.title}</strong>
                                <time>
                                  {new Date(task.starts_at).toLocaleString(
                                    "pt-PT",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </time>
                                {canManageTasks && <small className="task-assignee">{assignees.find((person) => person.user_id === task.assigned_to)?.display_name ?? "Comercial não identificado"}</small>}
                              </div>
                              <span>
                                {group.key === "overdue"
                                  ? "Atrasada"
                                  : group.key === "pending"
                                    ? task.status === "in_progress"
                                      ? "Em curso"
                                      : "Agendada"
                                    : group.key === "completed"
                                      ? "Concluída"
                                      : group.key === "withdrawn"
                                        ? "A rever"
                                        : "Revista"}
                              </span>
                            </button>
                          ))}
                        </section>
                      ),
                  )
                )}
                {totalTasks > pageSize && (
                  <nav className="task-pagination" aria-label="Paginação das tarefas">
                    <button type="button" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>Anterior</button>
                    <span>{page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalTasks)} de {totalTasks}</span>
                    <button type="button" disabled={(page + 1) * pageSize >= totalTasks} onClick={() => setPage((current) => current + 1)}>Seguinte</button>
                  </nav>
                )}
              </div>
              {selected && (
                <div className="task-detail-backdrop" onClick={() => setSelected(null)}>
                  <div className="task-detail-panel" role="dialog" aria-modal="true" aria-label={`Detalhes da tarefa ${selected.title}`} onClick={(event) => event.stopPropagation()}>
                  <form key={selected.id} onSubmit={save}>
                    <header className="task-detail-header">
                      <div><p className="eyebrow">Detalhes</p><h3>{selected.title}</h3></div>
                      <div className="task-detail-header-actions">
                        {(selected.status === "open" || selected.status === "in_progress") && (
                          <select
                            className="task-action-menu"
                            aria-label="Ações da tarefa"
                            defaultValue=""
                            onChange={(event) => {
                              const action = event.currentTarget.value;
                              event.currentTarget.value = "";
                              if (action === "finish") void finish();
                              if (action === "withdraw") setTaskAction("withdraw");
                              if (action === "reschedule") {
                                const field = event.currentTarget.form?.elements.namedItem("starts_at");
                                if (field instanceof HTMLInputElement) field.focus();
                              }
                            }}
                          >
                            <option value="" disabled>Ações</option>
                            <option value="finish">Marcar como feita</option>
                            <option value="reschedule">Reagendar</option>
                            <option value="withdraw">Desistir</option>
                          </select>
                        )}
                        <button type="button" className="icon-button" aria-label="Fechar detalhes da tarefa" onClick={() => setSelected(null)}>×</button>
                      </div>
                    </header>
                    <label>
                      <span>Tarefa</span>
                      <input
                        name="title"
                        defaultValue={selected.title}
                        required
                      />
                    </label>
                    <label>
                      <span>Informação</span>
                      <textarea
                        name="description"
                        rows={4}
                        defaultValue={selected.description ?? ""}
                      />
                    </label>
                    <div className="task-date-grid">
                      <label>
                        <span>Início</span>
                        <input
                          name="starts_at"
                          type="datetime-local"
                          defaultValue={isoLocal(new Date(selected.starts_at))}
                          required
                        />
                      </label>
                      <label>
                        <span>Fim</span>
                        <input
                          name="ends_at"
                          type="datetime-local"
                          defaultValue={
                            selected.ends_at
                              ? isoLocal(new Date(selected.ends_at))
                              : ""
                          }
                        />
                      </label>
                    </div>
                    <label>
                      <span>Prioridade</span>
                      <select name="priority" defaultValue={selected.priority}>
                        <option value="urgent">Urgente</option>
                        <option value="high">Alta</option>
                        <option value="normal">Normal</option>
                        <option value="low">Baixa</option>
                      </select>
                    </label>
                    {selected.withdrawal_reason && (
                      <div className="archive-reason">
                        <strong>Motivo da desistência</strong>
                        <span>{selected.withdrawal_reason}</span>
                      </div>
                    )}
                    <div className="task-detail-actions">
                      {selected.status === "withdrawn" ? (
                        <>
                          <button type="button" className="primary-button compact" onClick={() => setTaskAction("reactivate")}>Reativar</button>
                          {canManageTasks && selected.withdrawal_review_status === "pending" && (
                            <button type="button" className="text-button" onClick={() => setTaskAction("approve")}>Aprovar desistência</button>
                          )}
                        </>
                      ) : selected.status === "completed" ? (
                        canManageTasks && <button type="button" className="primary-button compact" onClick={() => setTaskAction("reopen")}>Reabrir tarefa</button>
                      ) : (
                        <>
                          <button
                            type="submit"
                            className="primary-button compact"
                          >
                            Guardar
                          </button>
                        </>
                      )}
                    </div>
                    <section className="task-history">
                      <h4>Histórico da tarefa</h4>
                      {events.length === 0 ? <p>Sem movimentos registados.</p> : events.map((item) => <article key={item.id}><i /><div><strong>{{created:"Criada",updated:"Alterada",rescheduled:"Reagendada",completed:"Concluída",withdrawn:"Desistência registada",withdrawal_approved:"Desistência aprovada",reactivated:"Reativada",reopened:"Tarefa reaberta"}[item.event_type] ?? item.event_type}</strong><span>{assignees.find((person) => person.user_id === item.actor_user_id)?.display_name ?? "Utilizador"} · {new Date(item.created_at).toLocaleString("pt-PT")}</span>{item.message && <p>{item.message}</p>}</div></article>)}
                    </section>
                    </form>
                  </div>
                </div>
              )}
              {taskAction && selected && (
                <div className="task-action-backdrop" onClick={() => setTaskAction(null)}>
                  <form className="task-action-dialog" role="dialog" aria-modal="true" aria-label="Confirmar ação da tarefa" onSubmit={submitTaskAction} onClick={(event) => event.stopPropagation()}>
                    <header><div><p className="eyebrow">Ação da tarefa</p><h3>{{withdraw:"Desistir da tarefa",reactivate:"Reativar tarefa",approve:"Aprovar desistência",reopen:"Reabrir tarefa concluída"}[taskAction]}</h3></div><button type="button" className="icon-button" onClick={() => setTaskAction(null)}>×</button></header>
                    <p>{{withdraw:"Explique por que motivo não irá executar esta tarefa. A desistência ficará pendente de revisão.",reactivate:"Deixe uma orientação para o comercial responsável continuar o acompanhamento.",approve:"Registe uma nota de revisão. A tarefa continuará preservada no histórico.",reopen:"Justifique por que motivo uma tarefa já concluída deve voltar à agenda."}[taskAction]}</p>
                    <label><span>{taskAction === "withdraw" || taskAction === "reopen" ? "Justificação obrigatória" : "Mensagem"}</span><textarea name="note" rows={4} required={taskAction !== "approve"} autoFocus defaultValue={taskAction === "reactivate" ? "Tarefa reativada para novo acompanhamento." : taskAction === "approve" ? "Desistência revista e aprovada." : ""} /></label>
                    <footer><button type="button" className="text-button" onClick={() => setTaskAction(null)}>Cancelar</button><button className="primary-button compact" disabled={actionSaving}>{actionSaving ? "A guardar…" : "Confirmar"}</button></footer>
                  </form>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function CommercialWorkspace({
  userId,
  locale,
  isMaster,
}: {
  userId: string;
  locale: SupportedLocale;
  isMaster: boolean;
}) {
  const [tab, setTab] = useState<Tab>("leads"),
    [leads, setLeads] = useState<Lead[]>([]),
    [tasks, setTasks] = useState<Task[]>([]),
    [plans, setPlans] = useState<Plan[]>([]),
    [loading, setLoading] = useState(true),
    [showLead, setShowLead] = useState(false),
    [showTask, setShowTask] = useState(false),
    [selectedLead, setSelectedLead] = useState<Lead | null>(null),
    [scope, setScope] = useState(isMaster ? "all" : "mine"),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const [l, t, p] = await Promise.all([
      supabase
        .from("leads")
        .select(
          "id,organization_id,company_name,contact_name,email,phone,city,country_code,status,priority,assigned_to,created_by,updated_at,archive_reason",
        )
        .order("updated_at", { ascending: false }),
      supabase
        .from("commercial_tasks")
        .select(
          "id,lead_id,organization_id,title,description,starts_at,ends_at,priority,status,assigned_to,archive_reason",
        )
        .order("starts_at"),
      supabase
        .from("plans")
        .select("id,name,description,is_trial,trial_days,active")
        .eq("active", true),
    ]);
    setLeads((l.data ?? []) as Lead[]);
    setTasks((t.data ?? []) as Task[]);
    setPlans((p.data ?? []) as Plan[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("vorno:tasks-changed"));
  }, [tasks]);
  useEffect(() => {
    if (!message) return;
    const id = window.setTimeout(() => setMessage(null), 10000);
    return () => window.clearTimeout(id);
  }, [message]);
  const visibleLeads = useMemo(
    () =>
      scope === "all"
        ? leads
        : leads.filter((x) => (x.assigned_to ?? x.created_by) === userId),
    [leads, scope, userId],
  );
  const visibleTasks = useMemo(
    () =>
      scope === "all" ? tasks : tasks.filter((x) => x.assigned_to === userId),
    [tasks, scope, userId],
  );
  const createLead = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      country = String(f.get("country_code"));
    const { error } = await supabase
      .from("leads")
      .insert({
        company_name: String(f.get("company_name")),
        contact_name: String(f.get("contact_name") || "") || null,
        email: String(f.get("email") || "") || null,
        phone: String(f.get("phone") || "") || null,
        country_code: country,
        locale: country === "BR" ? "pt-BR" : "pt-PT",
        currency_code: country === "BR" ? "BRL" : "EUR",
        status: "new",
        priority: String(f.get("priority")),
        assigned_to: userId,
        created_by: userId,
      });
    if (error) {
      setMessage(error.message);
      return;
    }
    setShowLead(false);
    setMessage("Lead criado.");
    void load();
  };
  const createTask = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      start = String(f.get("starts_at")),
      end = String(f.get("ends_at") || "");
    const { error } = await supabase
      .from("commercial_tasks")
      .insert({
        title: String(f.get("title")),
        description: String(f.get("description") || "") || null,
        lead_id: String(f.get("lead_id") || "") || null,
        starts_at: new Date(start).toISOString(),
        ends_at: end ? new Date(end).toISOString() : null,
        priority: String(f.get("priority")),
        status: "open",
        assigned_to: userId,
        created_by: userId,
      });
    if (error) {
      setMessage(error.message);
      return;
    }
    setShowTask(false);
    setMessage("Tarefa criada.");
    void load();
  };
  const updateLead = async (status: string) => {
    if (!selectedLead) return;
    const { error } = await supabase
      .from("leads")
      .update({ status })
      .eq("id", selectedLead.id);
    if (error) {
      setMessage(error.message);
      return;
    }
    setSelectedLead({ ...selectedLead, status });
    setMessage("Estado do lead atualizado.");
    void load();
  };
  const activateTrial = async () => {
    if (!selectedLead) return;
    const plan = plans.find((x) => x.is_trial);
    if (!plan) {
      setMessage("Ainda não existe um plano experimental ativo.");
      return;
    }
    const { error } = await supabase.rpc("activate_lead_plan", {
      p_lead_id: selectedLead.id,
      p_plan_id: plan.id,
      p_trial: true,
    });
    if (error) {
      setMessage(error.message);
      return;
    }
    setMessage("Período experimental de 7 dias ativado.");
    setSelectedLead(null);
    void load();
  };
  const taskAction = async (
    task: Task,
    action: "completed" | "archived" | "reschedule",
  ) => {
    if (action === "completed") {
      await supabase
        .from("commercial_tasks")
        .update({ status: "completed", completed_at: new Date().toISOString() })
        .eq("id", task.id);
    } else if (action === "archived") {
      const reason = window.prompt(
        "Indique obrigatoriamente o motivo do arquivo:",
      );
      if (!reason?.trim()) return;
      await supabase
        .from("commercial_tasks")
        .update({
          status: "archived",
          archived_at: new Date().toISOString(),
          archived_by: userId,
          archive_reason: reason.trim(),
        })
        .eq("id", task.id);
    } else {
      const next = window.prompt(
        "Nova data e hora (AAAA-MM-DDTHH:MM):",
        isoLocal(new Date(task.starts_at)),
      );
      if (!next) return;
      await supabase
        .from("commercial_tasks")
        .update({ starts_at: new Date(next).toISOString(), status: "open" })
        .eq("id", task.id);
    }
    setMessage("Tarefa atualizada.");
    void load();
  };
  return (
    <section className="commercial-workspace">
      {message && (
        <button className="notice" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      <div className="commercial-heading">
        <div>
          <p className="eyebrow">Gestão comercial</p>
          <h2>Comercial</h2>
          <p>
            Leads, etapas de venda e tarefas da equipa Vorno Driver num único lugar.
          </p>
        </div>
        {isMaster && (
          <label className="scope-filter">
            <span>Âmbito da visão</span>
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">Toda a operação</option>
              <option value="mine">Apenas os meus</option>
            </select>
          </label>
        )}
      </div>
      <nav className="workspace-tabs">
        <button
          className={tab === "leads" ? "active" : ""}
          onClick={() => setTab("leads")}
        >
          Leads
        </button>
        <button
          className={tab === "pipeline" ? "active" : ""}
          onClick={() => setTab("pipeline")}
        >
          Etapas de venda
        </button>
        <button
          className={tab === "tasks" ? "active" : ""}
          onClick={() => setTab("tasks")}
        >
          Tarefas
        </button>
      </nav>
      {loading ? (
        <div className="empty-state">A carregar…</div>
      ) : tab === "leads" ? (
        <>
          <div className="workspace-toolbar">
            <p>
              <strong>{visibleLeads.length}</strong>{" "}
              {visibleLeads.length === 1
                ? "oportunidade comercial em acompanhamento"
                : "oportunidades comerciais em acompanhamento"}
              .
            </p>
            <button
              className="primary-button compact"
              onClick={() => setShowLead(true)}
            >
              + Novo lead
            </button>
          </div>
          <div className="lead-list">
            {visibleLeads.map((lead) => (
              <article key={lead.id} onClick={() => setSelectedLead(lead)}>
                <div className="lead-avatar">
                  {lead.company_name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <strong>{lead.company_name}</strong>
                  <span>
                    {lead.contact_name ||
                      lead.email ||
                      "Contacto ainda não indicado"}
                  </span>
                </div>
                <span className={`lead-state state-${lead.status}`}>
                  {leadLabels[lead.status]}
                </span>
                <span className={`priority-tag priority-${lead.priority}`}>
                  {priorities[lead.priority]}
                </span>
                <small>{lead.city || lead.country_code}</small>
              </article>
            ))}
          </div>
        </>
      ) : tab === "pipeline" ? (
        <div className="pipeline-board">
          {[
            "new",
            "to_contact",
            "contacted",
            "qualifying",
            "meeting",
            "proposal",
            "trial",
            "won",
          ].map((status) => (
            <section key={status}>
              <header>
                <strong>{leadLabels[status]}</strong>
                <span>
                  {visibleLeads.filter((x) => x.status === status).length}
                </span>
              </header>
              {visibleLeads
                .filter((x) => x.status === status)
                .map((lead) => (
                  <article key={lead.id} onClick={() => setSelectedLead(lead)}>
                    <strong>{lead.company_name}</strong>
                    <span>{priorities[lead.priority]}</span>
                  </article>
                ))}
            </section>
          ))}
        </div>
      ) : (
        <>
          <div className="workspace-toolbar">
            <p>Crie tarefas associadas a leads ou tarefas autónomas.</p>
            <button
              className="primary-button compact"
              onClick={() => setShowTask(true)}
            >
              + Nova tarefa
            </button>
          </div>
          <div className="commercial-task-list">
            {visibleTasks.map((task) => (
              <article
                key={task.id}
                className={`priority-${task.priority} ${task.status}`}
              >
                <i />
                <div>
                  <strong>{task.title}</strong>
                  <span>
                    {new Date(task.starts_at).toLocaleString(locale)} ·{" "}
                    {priorities[task.priority]}
                  </span>
                  <small>{task.description || "Sem observações"}</small>
                </div>
                <div className="task-actions">
                  <button onClick={() => void taskAction(task, "reschedule")}>
                    Reagendar
                  </button>
                  <button onClick={() => void taskAction(task, "completed")}>
                    Concluir
                  </button>
                  <button onClick={() => void taskAction(task, "archived")}>
                    Arquivar
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
      {showLead && (
        <div className="modal-backdrop">
          <section className="crm-modal">
            <header>
              <div>
                <p className="eyebrow">Comercial</p>
                <h2>Novo lead</h2>
                <p className="dialog-intro">
                  Registe apenas o que já sabe. O restante pode ser completado
                  durante o acompanhamento.
                </p>
              </div>
              <button
                className="icon-button"
                onClick={() => setShowLead(false)}
              >
                ×
              </button>
            </header>
            <form onSubmit={createLead}>
              <div className="form-grid">
                <label>
                  <span>Nome do stand *</span>
                  <input name="company_name" required minLength={2} />
                </label>
                <label>
                  <span>Responsável</span>
                  <input name="contact_name" />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" type="email" />
                </label>
                <label>
                  <span>Telefone</span>
                  <input name="phone" />
                </label>
                <label>
                  <span>País</span>
                  <select name="country_code">
                    <option value="PT">Portugal</option>
                    <option value="BR">Brasil</option>
                  </select>
                </label>
                <label>
                  <span>Prioridade</span>
                  <select name="priority">
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                    <option value="low">Baixa</option>
                  </select>
                </label>
              </div>
              <footer>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setShowLead(false)}
                >
                  Cancelar
                </button>
                <button className="primary-button compact">Criar lead</button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {showTask && (
        <div className="modal-backdrop">
          <section className="crm-modal">
            <header>
              <div>
                <p className="eyebrow">Agenda</p>
                <h2>Nova tarefa</h2>
                <p className="dialog-intro">
                  A tarefa pode existir sozinha ou ficar associada a um lead.
                </p>
              </div>
              <button
                className="icon-button"
                onClick={() => setShowTask(false)}
              >
                ×
              </button>
            </header>
            <form onSubmit={createTask}>
              <div className="form-grid">
                <label className="wide">
                  <span>Tarefa *</span>
                  <input
                    name="title"
                    required
                    minLength={2}
                    placeholder="Ex.: Ligar ao stand e marcar uma visita"
                  />
                </label>
                <label>
                  <span>Início *</span>
                  <input
                    name="starts_at"
                    type="datetime-local"
                    defaultValue={isoLocal()}
                    required
                  />
                </label>
                <label>
                  <span>Fim</span>
                  <input name="ends_at" type="datetime-local" />
                </label>
                <label>
                  <span>Prioridade</span>
                  <select name="priority">
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                    <option value="low">Baixa</option>
                  </select>
                </label>
                <label>
                  <span>Lead relacionado</span>
                  <select name="lead_id">
                    <option value="">Sem lead associado</option>
                    {visibleLeads
                      .filter(
                        (x) => !["won", "lost", "archived"].includes(x.status),
                      )
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.company_name}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="wide">
                  <span>Informação</span>
                  <textarea name="description" rows={3} />
                </label>
              </div>
              <footer>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setShowTask(false)}
                >
                  Cancelar
                </button>
                <button className="primary-button compact">Criar tarefa</button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {selectedLead && (
        <div className="modal-backdrop">
          <section className="crm-modal lead-detail">
            <header>
              <div>
                <p className="eyebrow">Lead comercial</p>
                <h2>{selectedLead.company_name}</h2>
                <p className="dialog-intro">
                  Acompanhe a oportunidade até ao período experimental ou à
                  contratação.
                </p>
              </div>
              <button
                className="icon-button"
                onClick={() => setSelectedLead(null)}
              >
                ×
              </button>
            </header>
            <div className="lead-summary">
              <span>{selectedLead.country_code}</span>
              <span>{priorities[selectedLead.priority]}</span>
              <span>{leadLabels[selectedLead.status]}</span>
            </div>
            <label className="standalone-field">
              <span>Etapa da venda</span>
              <select
                value={selectedLead.status}
                onChange={(e) => void updateLead(e.target.value)}
              >
                {Object.keys(leadLabels)
                  .filter((x) => x !== "archived")
                  .map((x) => (
                    <option key={x} value={x}>
                      {leadLabels[x]}
                    </option>
                  ))}
              </select>
            </label>
            <div className="conversion-panel">
              <strong>Transformar em stand cliente</strong>
              <p>
                Confirme primeiro os dados empresariais. O sistema preservará
                todo o histórico deste lead.
              </p>
              <button
                className="primary-button compact"
                onClick={() => void activateTrial()}
              >
                Ativar período experimental de 7 dias
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
